"use client";

import PortableText from "@/components/PortableText";
import { TranslationData } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import type { PortableTextProps } from "next-sanity";
import type { CSSProperties, ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";

const OVERFLOW_EPSILON = 2;

export type PortableTextValue = NonNullable<PortableTextProps["value"]>;

export type ExpandableLineClampContentProps = {
  lineClamp?: number;
  translations?: TranslationData;
  className?: string;
} & (
  | { portableTextValue: PortableTextValue; children?: never }
  | { children: ReactNode; portableTextValue?: never }
);

const clampStyle = (lines: number): CSSProperties => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lines,
  overflow: "hidden",
});

/**
 * Line-clamps content (default 6 lines) and toggles full text with Read more / Show less.
 */
export default function ExpandableLineClampContent({
  lineClamp = 6,
  translations,
  className,
  portableTextValue,
  children,
}: ExpandableLineClampContentProps) {
  const lines = Math.max(1, Math.floor(lineClamp));
  const [expanded, setExpanded] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const readMoreLabel =
    translations?.common?.common_read_more_label ?? "Read More";
  const showLessLabel =
    translations?.common?.common_show_less_label ?? "Show Less";

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      if (expanded) {
        setShowControls(true);
        return;
      }
      setShowControls(el.scrollHeight > el.clientHeight + OVERFLOW_EPSILON);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, portableTextValue, children, lines]);

  const body =
    portableTextValue !== undefined ? (
      <PortableText value={portableTextValue} />
    ) : (
      children
    );

  return (
    <div className={cn(className)}>
      <div
        ref={contentRef}
        className="break-words"
        style={expanded ? undefined : clampStyle(lines)}
      >
        {body}
      </div>
      {showControls && (
        <button
          type="button"
          className="mt-1 cursor-pointer text-left text-sm font-semibold underline underline-offset-2 md:text-base"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? showLessLabel : readMoreLabel}
        </button>
      )}
    </div>
  );
}
